import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { VoucherNumberInput } from "@/components/VoucherNumberInput";
import { Play, Wrench, Trash2, ArrowRightLeft, CheckCircle2, X, Plus, ArrowRight, Search } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import UserLabel, { useUserDirectory } from "@/components/UserLabel";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  ["register", "Asset Register"],
  ["entries", "Depreciation"],
  ["adjustments", "Adjustments"],
  ["repairs", "Repairs"],
  ["scrap", "Scrap"],
  ["scrap_sales", "Scrap Sales"],
  ["transfers", "Asset Transfers"],
  ["movements", "Movement History"],
];

export default function AssetLifecyclePage() {
  useUserDirectory();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("register");
  const [search, setSearch] = useState("");
  const visibleTabs = TABS.filter(([k]) => k !== "scrap_sales" || hasPerm("scrap_sale", "view"));

  // Depreciation
  const [register, setRegister] = useState(null);
  const [entries, setEntries] = useState([]);
  const [periodType, setPeriodType] = useState("Monthly");
  const [periodDate, setPeriodDate] = useState(new Date().toISOString().slice(0, 10));
  const [running, setRunning] = useState(false);

  // Lists
  const [repairs, setRepairs] = useState([]);
  const [scraps, setScraps] = useState([]);
  const [scrapSales, setScrapSales] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [movements, setMovements] = useState([]);
  // Sprint 2026-Q1d Step ⑥ — Period Adjustment Journal. Append-only;
  // posts can be created from the modal and reversed (which posts a
  // NEW row with opposite delta). Reports = snapshots + adjustments.
  const [adjustments, setAdjustments] = useState([]);
  const [openAdj, setOpenAdj] = useState(false);

  // Modals
  const [openRepair, setOpenRepair] = useState(false);
  const [openScrap, setOpenScrap] = useState(false);
  const [openScrapSale, setOpenScrapSale] = useState(false);
  const [openTransfer, setOpenTransfer] = useState(false);

  const loadRegister = () => api.get("/depreciation/register").then(r => setRegister(r.data));
  const loadEntries = () => api.get("/depreciation/entries").then(r => setEntries(r.data));
  const loadRepairs = () => api.get("/asset-repairs").then(r => setRepairs(r.data));
  const loadScraps = () => api.get("/asset-scraps").then(r => setScraps(r.data));
  const loadScrapSales = () => api.get("/scrap-sales").then(r => setScrapSales(r.data)).catch(() => setScrapSales([]));
  const loadTransfers = () => api.get("/asset-transfers").then(r => setTransfers(r.data));
  const loadMovements = () => api.get("/asset-movements").then(r => setMovements(r.data));
  const loadAdjustments = () => api.get("/depreciation/adjustments?limit=500")
    .then(r => {
      const data = r.data;
      setAdjustments(Array.isArray(data) ? data : (data?.items || []));
    })
    .catch(() => setAdjustments([]));

  useEffect(() => {
    loadRegister(); loadEntries(); loadRepairs(); loadScraps(); loadScrapSales();
    loadTransfers(); loadMovements(); loadAdjustments();
  }, []);

  async function runDep() {
    setRunning(true);
    try {
      const r = await api.post(`/depreciation/run?period_type=${periodType}&period_date=${periodDate}`);
      toast.success(`Dep run · period ${r.data.period_key} · ${r.data.posted} posted, ${r.data.skipped} skipped`);
      loadRegister(); loadEntries();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  }

  // Search filter applied per active tab
  const match = (obj, fields) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return fields.some(f => (obj?.[f] || "").toString().toLowerCase().includes(q));
  };
  const regFiltered = useMemo(
    () => (register?.rows || []).filter(r => match(r, ["asset_code", "asset_name", "category", "branch_name", "method", "status"])),
    [register, search]
  );
  const entriesFiltered = useMemo(
    () => entries.filter(e => match(e, ["period_key", "asset_code", "asset_name", "method", "project_name", "branch_name"])),
    [entries, search]
  );
  const repairsFiltered = useMemo(
    () => repairs.filter(r => match(r, ["voucher_number", "asset_code", "asset_name", "vendor_name", "project_name", "invoice_ref", "description"])),
    [repairs, search]
  );
  const scrapsFiltered = useMemo(
    () => scraps.filter(s => match(s, ["voucher_number", "asset_code", "asset_name", "reason", "sale_voucher_number"])),
    [scraps, search]
  );
  const scrapSalesFiltered = useMemo(
    () => scrapSales.filter(s => match(s, ["voucher_number", "buyer_name", "buyer_contact", "remarks"])),
    [scrapSales, search]
  );
  const transfersFiltered = useMemo(
    () => transfers.filter(t => match(t, ["transfer_number", "from_branch_name", "to_branch_name", "vehicle_no", "driver_name", "lr_no", "status"])),
    [transfers, search]
  );
  const movementsFiltered = useMemo(
    () => movements.filter(m => match(m, ["asset_code", "movement_type", "from_member_name", "to_member_name", "to_location", "reference_no", "remarks"])),
    [movements, search]
  );
  const adjustmentsFiltered = useMemo(
    () => adjustments.filter(a => match(a, [
      "period_key", "asset_code", "asset_name", "project_name",
      "branch_name", "cost_centre_name", "business_vertical_name",
      "reason", "reference_doc", "posted_by",
    ])),
    [adjustments, search]
  );

  // Client-side pagination for each tab's filtered view
  const regPg       = useClientPagination(regFiltered,       { initialPageSize: 50 });
  const entriesPg   = useClientPagination(entriesFiltered,   { initialPageSize: 50 });
  const repairsPg   = useClientPagination(repairsFiltered,   { initialPageSize: 50 });
  const scrapsPg    = useClientPagination(scrapsFiltered,    { initialPageSize: 50 });
  const scrapSalesPg = useClientPagination(scrapSalesFiltered, { initialPageSize: 50 });
  const transfersPg = useClientPagination(transfersFiltered, { initialPageSize: 50 });
  const movementsPg = useClientPagination(movementsFiltered, { initialPageSize: 50 });
  const adjustmentsPg = useClientPagination(adjustmentsFiltered, { initialPageSize: 50 });

  const tabPlaceholders = {
    register: "Search by asset code / name / category / branch / method…",
    entries: "Search by period / asset / method / project…",
    adjustments: "Search by period / asset / project / cost centre / vertical / reason / ref…",
    repairs: "Search by voucher / asset / vendor / project / ref…",
    scrap: "Search by voucher / asset / reason / sale voucher…",
    scrap_sales: "Search by sale voucher / buyer / remarks…",
    transfers: "Search by transfer / branch / vehicle / driver / LR / status…",
    movements: "Search by asset / type / from / to / reference…",
  };

  async function approveTransfer(id) { try { await api.post(`/asset-transfers/${id}/approve`); toast.success("Approved · In-Transit"); loadTransfers(); loadRegister(); loadMovements(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }
  async function receiveTransfer(id) { try { await api.post(`/asset-transfers/${id}/receive`); toast.success("Received at destination"); loadTransfers(); loadRegister(); loadMovements(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }
  async function cancelTransfer(id) { if (!confirm("Cancel this asset transfer?")) return; try { await api.post(`/asset-transfers/${id}/cancel`); toast.success("Cancelled"); loadTransfers(); loadRegister(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Assets"
        title="Asset/Inventory Lifecycle &amp; Depreciation"
        subtitle="SLM/WDV depreciation engine · Repairs · Scrap · Inter-branch transfers · Movement history."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {tab === "entries" || tab === "register" ? (
              <>
                <select data-testid="dep-period-type" value={periodType} onChange={e => setPeriodType(e.target.value)} className="border border-slate-300 px-2 py-2 text-xs">
                  <option value="Monthly">Monthly</option><option value="Yearly">Yearly</option>
                </select>
                <input data-testid="dep-period-date" type="date" value={periodDate} onChange={e => setPeriodDate(e.target.value)} className="border border-slate-300 px-2 py-2 text-xs" />
                <button data-testid="dep-run-btn" onClick={runDep} disabled={running}
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
                  <Play size={13} /> {running ? "Running…" : "Run Depreciation"}
                </button>
              </>
            ) : null}
            {tab === "repairs" && (
              <button data-testid="new-repair-btn" onClick={() => setOpenRepair(true)}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"><Plus size={14} /> New Repair</button>
            )}
            {tab === "scrap" && (
              <button data-testid="new-scrap-btn" onClick={() => setOpenScrap(true)}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"><Plus size={14} /> New Scrap</button>
            )}
            {tab === "scrap_sales" && hasPerm("scrap_sale", "export") && (
              <button data-testid="scrap-sales-export-btn" onClick={async () => {
                try {
                  const res = await api.get("/scrap-sales/export.xlsx", { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a"); a.href = url; a.download = "scrap_sales_register.xlsx";
                  document.body.appendChild(a); a.click(); a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 30000);
                } catch { toast.error("Export failed"); }
              }}
                className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">Excel</button>
            )}
            {tab === "scrap_sales" && hasPerm("scrap_sale", "create") && (
              <button data-testid="new-scrap-sale-btn" onClick={() => setOpenScrapSale(true)}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"><Plus size={14} /> New Scrap Sale</button>
            )}
            {tab === "transfers" && (
              <button data-testid="new-asset-transfer-btn" onClick={() => setOpenTransfer(true)}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"><Plus size={14} /> New Transfer</button>
            )}
            {tab === "adjustments" && (
              <button data-testid="new-adjustment-btn" onClick={() => setOpenAdj(true)}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2"><Plus size={14} /> New Adjustment</button>
            )}
          </div>
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto" data-testid="al-tabs">
        {visibleTabs.map(([k, l]) => (
          <button key={k} data-testid={`al-tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-[480px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="al-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={tabPlaceholders[tab]}
            className="w-full border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316]" />
        </div>
        {search && (
          <button onClick={() => setSearch("")} className="text-xs text-slate-500 hover:text-[#F97316] underline">Clear</button>
        )}
      </div>

      {tab === "register" && (register
        ? (register.rows.length === 0 ? <EmptyState title="No assets yet" />
          : regFiltered.length === 0 ? <EmptyState title="No matches" hint="Clear the search to see all assets." />
          : (
            <Card className="overflow-hidden">
              <div className="bg-slate-50/70 border-b px-5 py-3 flex flex-wrap justify-end gap-6 text-sm">
                <div><span className="text-slate-400 mr-1">FY:</span><span className="font-bold tabular">{register.fy_code?.code}</span></div>
                <div><span className="text-slate-400 mr-1">PV:</span><span className="font-bold tabular">{formatINR(register.totals.purchase_value)}</span></div>
                <div><span className="text-slate-400 mr-1">Acc. Dep:</span><span className="font-bold text-red-700 tabular">{formatINR(register.totals.accumulated_depreciation)}</span></div>
                <div><span className="text-slate-400 mr-1">Net Block:</span><span className="font-bold text-[#F97316] tabular">{formatINR(register.totals.net_block)}</span></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="dep-register-table">
                  <thead className="bg-slate-50 border-b-2 border-slate-200">
                    <tr>{["Asset Code", "Name", "Category", "Branch", "Warehouse", "Method", "Yrs / Rate", "Purchase Value", "Acc. Dep", "Net Block", "Status"].map(h =>
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                    )}</tr>
                  </thead>
                  <tbody>
                    {regPg.paged.map(r => (
                      <tr key={r.asset_id} className="border-b border-slate-100">
                        <td className="py-3 px-4 text-xs font-mono font-bold text-[#F97316]">{r.asset_code}</td>
                        <td className="py-3 px-4 text-sm">{r.asset_name}</td>
                        <td className="py-3 px-4 text-xs text-slate-500">{r.category || "—"}</td>
                        <td className="py-3 px-4 text-xs text-slate-500">{r.branch_name || "—"}</td>
                        <td className="py-3 px-4 text-xs text-slate-600 font-bold">{r.warehouse_name || <span className="text-amber-600 font-normal">—</span>}</td>
                        <td className="py-3 px-4 text-xs uppercase font-bold">{r.method}</td>
                        <td className="py-3 px-4 text-xs">{r.method === "SLM" ? `${r.useful_life_years} yrs` : `${r.depreciation_rate}%`}</td>
                        <td className="py-3 px-4 text-sm tabular">{formatINR(r.purchase_value)}</td>
                        <td className="py-3 px-4 text-sm tabular text-red-700">{formatINR(r.accumulated_depreciation)}</td>
                        <td className="py-3 px-4 text-sm tabular font-bold text-[#F97316]">{formatINR(r.net_block)}</td>
                        <td className="py-3 px-4"><StatusBadge status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationControls page={regPg.page} pageSize={regPg.pageSize} total={regPg.total}
                onPage={regPg.setPage} onPageSize={regPg.setPageSize}
                pageSizeOptions={[25, 50, 100, 200]} />
            </Card>
          ))
        : null)}

      {tab === "entries" && (entries.length === 0
        ? <EmptyState title="No depreciation entries yet" hint="Set the period and click Run Depreciation above." />
        : entriesFiltered.length === 0
        ? <EmptyState title="No matches" hint="Clear the search to see all entries." />
        : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dep-entries-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Period", "Asset", "Method", "Opening", "Depreciation", "Closing", "Project", "Branch", "Posted"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {entriesPg.paged.map(e => (
                    <tr key={e.id} className="border-b border-slate-100">
                      <td className="py-3 px-4 text-xs font-mono font-bold">{e.period_key}</td>
                      <td className="py-3 px-4 text-sm">{e.asset_code} · {e.asset_name}</td>
                      <td className="py-3 px-4 text-xs uppercase">{e.method}</td>
                      <td className="py-3 px-4 text-sm tabular">{formatINR(e.opening_net_block)}</td>
                      <td className="py-3 px-4 text-sm tabular text-red-700">{formatINR(e.depreciation)}</td>
                      <td className="py-3 px-4 text-sm tabular font-bold">{formatINR(e.closing_net_block)}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">{e.project_name || "—"}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">{e.branch_name || "—"}</td>
                      <td className="py-3 px-4 text-[10px] text-slate-400">{(e.posted_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={entriesPg.page} pageSize={entriesPg.pageSize} total={entriesPg.total}
              onPage={entriesPg.setPage} onPageSize={entriesPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        ))}

      {/* Sprint 2026-Q1d Step ⑥ — Period Adjustment Journal */}
      {tab === "adjustments" && (adjustments.length === 0
        ? <EmptyState
            title="No depreciation adjustments yet"
            hint="Adjustments correct a CLOSED period without touching the immutable snapshot. Click ‘New Adjustment’ above to post one." />
        : adjustmentsFiltered.length === 0
        ? <EmptyState title="No matches" hint="Clear the search to see all adjustments." />
        : (
          <Card className="overflow-hidden">
            <div className="bg-amber-50/40 border-b border-amber-200 px-5 py-2 text-xs text-amber-900">
              <b>Append-only journal.</b> Adjustments add to (or offset) the
              immutable snapshot for a period. To undo a posted adjustment,
              click <i>Reverse</i> — that posts a new entry with the opposite
              delta. The original row is never edited or deleted.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dep-adjustments-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Posted", "Period", "Asset", "Project", "Cost Centre", "Vertical", "Δ Depreciation", "Reason", "By", ""].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {adjustmentsPg.paged.map(a => (
                    <tr key={a.id}
                        data-testid={`adj-row-${a.id}`}
                        className={`border-b border-slate-100 ${a.reverses_adjustment_id ? "bg-rose-50/40" : ""}`}>
                      <td className="py-3 px-4 text-[10px] text-slate-400 whitespace-nowrap">
                        {(a.posted_at || "").slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="py-3 px-4 text-xs font-mono font-bold">{a.period_key}</td>
                      <td className="py-3 px-4 text-sm">
                        {a.asset_code
                          ? <span><span className="font-mono font-bold text-[#F97316]">{a.asset_code}</span>{a.asset_name ? <span className="text-slate-400 ml-1">· {a.asset_name}</span> : null}</span>
                          : <span className="text-slate-400 italic">Overhead / non-asset</span>}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-600">{a.project_name || "—"}</td>
                      <td className="py-3 px-4 text-xs text-slate-600">{a.cost_centre_name || "—"}</td>
                      <td className="py-3 px-4 text-xs text-slate-600">{a.business_vertical_name || "—"}</td>
                      <td className={`py-3 px-4 text-sm tabular font-bold ${a.delta >= 0 ? "text-red-700" : "text-emerald-700"}`}>
                        {a.delta >= 0 ? "+" : ""}{formatINR(a.delta)}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-700 max-w-[280px]">
                        <div className="truncate" title={a.reason}>{a.reason}</div>
                        {a.reference_doc && (
                          <div className="text-[10px] text-slate-400 font-mono">ref: {a.reference_doc}</div>
                        )}
                        {a.reverses_adjustment_id && (
                          <div className="text-[10px] text-rose-700 font-bold mt-0.5">↩ reversing {a.reverses_adjustment_id.slice(0, 8)}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-[10px] text-slate-500"><UserLabel value={a.posted_by} fallback="—" /></td>
                      <td className="py-3 px-4 text-xs whitespace-nowrap">
                        {!a.reverses_adjustment_id && (
                          <button
                            data-testid={`adj-reverse-${a.id}`}
                            onClick={async () => {
                              if (!window.confirm(`Reverse adjustment ${a.id.slice(0, 8)}?\n\nThis posts a NEW row with delta = ${(-a.delta).toFixed(2)}. The original row stays as-is.`)) return;
                              try {
                                await api.post(`/depreciation/adjustments/${a.id}/reverse`, {});
                                toast.success("Reversing entry posted.");
                                loadAdjustments();
                              } catch (e) {
                                toast.error(e.response?.data?.detail || "Reverse failed.");
                              }
                            }}
                            className="text-rose-700 hover:underline font-bold">
                            Reverse
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={adjustmentsPg.page} pageSize={adjustmentsPg.pageSize} total={adjustmentsPg.total}
              onPage={adjustmentsPg.setPage} onPageSize={adjustmentsPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        ))}

      {tab === "repairs" && (repairs.length === 0
        ? <EmptyState title="No repair vouchers yet" hint="Log repair costs against an asset — choose ‘capitalised’ to add to Net Block. Needs-Repair inventory returns also land here." />
        : repairsFiltered.length === 0
        ? <EmptyState title="No matches" hint="Clear the search to see all repairs." />
        : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="repairs-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["RRN", "Sent", "Type", "Asset / Item", "Vendor", "Cost", "PI Invoice", "Received", "Status", "Assigned After", ""].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {repairsPg.paged.map(r => (
                    <tr key={r.id} className="border-b border-slate-100">
                      <td className="py-3 px-3 text-xs font-mono font-bold text-[#F97316]">{r.voucher_number}</td>
                      <td className="py-3 px-3 text-xs">{(r.voucher_date || "").slice(0, 10)}</td>
                      <td className="py-3 px-3 text-xs" data-testid={`repair-type-${r.id}`}>
                        <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${r.item_type === "Inventory" ? "bg-sky-50 text-sky-700 border-sky-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                          {r.item_type === "Inventory" ? "Inventory" : "Asset"}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm">{r.asset_code}<div className="text-[10px] text-slate-400">{r.asset_name}{r.item_type === "Inventory" ? ` · ${r.quantity} ${r.unit || ""}` : ""}</div></td>
                      <td className="py-3 px-3 text-xs">{r.vendor_name || "—"}</td>
                      <td className="py-3 px-3 text-sm tabular font-bold">{formatINR(r.repair_cost)}</td>
                      <td className="py-3 px-3 text-xs font-mono">{r.linked_pi_number || <span className="text-slate-400 italic">—</span>}</td>
                      <td className="py-3 px-3 text-xs">{(r.repair_completed_at || "").slice(0, 10) || "—"}</td>
                      <td className="py-3 px-3 text-[10px]">
                        {r.repair_completed_at
                          ? <span className="px-2 py-0.5 font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200">Completed</span>
                          : r.marks_in_repair
                            ? <span className="px-2 py-0.5 font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">In Repair</span>
                            : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-3 px-3 text-xs">
                        {r.post_repair_assign_name
                          ? <span className="text-emerald-700"><b>{r.post_repair_assign_type}</b><br /><span className="text-slate-500 font-normal">{r.post_repair_assign_name}</span></span>
                          : r.repair_completed_at ? <span className="text-slate-500">{r.item_type === "Inventory" ? "Restocked" : "Branch stock"}</span> : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-3 px-3 text-xs whitespace-nowrap">
                        {r.marks_in_repair && !r.repair_completed_at && (
                          <button data-testid={`repair-mark-ready-${r.id}`} onClick={async () => {
                            try { await api.post(`/asset-repairs/${r.id}/mark-ready`); toast.success(r.item_type === "Inventory" ? "Repair complete — qty restocked to inventory" : "Asset back to Available"); loadRepairs(); loadRegister(); loadMovements(); }
                            catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                          }} className="text-emerald-700 hover:underline font-bold">Mark Ready</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={repairsPg.page} pageSize={repairsPg.pageSize} total={repairsPg.total}
              onPage={repairsPg.setPage} onPageSize={repairsPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        ))}

      {tab === "scrap" && (scraps.length === 0
        ? <EmptyState title="No scrap vouchers yet" hint="Scrapping an asset writes-off its book value to P&amp;L (less any salvage value). Inventory returned as Lost or Damaged on a Return Voucher also lands here for scrap sale." />
        : scrapsFiltered.length === 0
        ? <EmptyState title="No matches" hint="Clear the search to see all scrap vouchers." />
        : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="scraps-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Voucher No", "Date", "Type", "Item / Asset", "Book Value", "Salvage", "Net Loss", "Reason", "Sold", "By"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {scrapsPg.paged.map(s => (
                    <tr key={s.id} className="border-b border-slate-100">
                      <td className="py-3 px-4 text-xs font-mono font-bold text-[#F97316]">{s.voucher_number}</td>
                      <td className="py-3 px-4 text-xs">{(s.voucher_date || "").slice(0, 10)}</td>
                      <td className="py-3 px-4">
                        {s.item_type === "Inventory"
                          ? <span data-testid={`scrap-type-${s.id}`} className="inline-block bg-sky-100 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[10px] font-bold uppercase">Inventory</span>
                          : <span data-testid={`scrap-type-${s.id}`} className="inline-block bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase">Asset</span>}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {s.item_type === "Inventory" ? (
                          <>{s.asset_name}<div className="text-[10px] text-slate-400">{s.quantity} {s.unit}{s.unit_rate ? ` @ ${formatINR(s.unit_rate)}` : ""}{s.source_return_voucher ? ` · ${s.source_return_voucher}` : ""}</div></>
                        ) : (
                          <>{s.asset_code}<div className="text-[10px] text-slate-400">{s.asset_name}</div></>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm tabular">{formatINR(s.book_value_at_scrap)}</td>
                      <td className="py-3 px-4 text-sm tabular text-emerald-700">{formatINR(s.salvage_recovered)}</td>
                      <td className="py-3 px-4 text-sm tabular font-bold text-red-700">{formatINR(s.net_loss)}</td>
                      <td className="py-3 px-4 text-xs text-slate-600">{s.reason || "—"}</td>
                      <td className="py-3 px-4 text-xs">
                        {s.sold
                          ? <span className="inline-block bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-bold uppercase">{s.sale_voucher_number}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-3 px-4 text-[10px] text-slate-400"><UserLabel value={s.created_by} fallback="—" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={scrapsPg.page} pageSize={scrapsPg.pageSize} total={scrapsPg.total}
              onPage={scrapsPg.setPage} onPageSize={scrapsPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        ))}

      {tab === "scrap_sales" && (scrapSales.length === 0
        ? <EmptyState title="No scrap sales yet" hint="Batch-sell scrapped assets to a buyer — clears them from the warehouse. The JV for sale proceeds is passed manually." />
        : scrapSalesFiltered.length === 0
        ? <EmptyState title="No matches" hint="Clear the search to see all scrap sales." />
        : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="scrap-sales-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Sale Voucher", "Date", "Buyer", "Items", "Total Sale Value", "Status", "Remarks", "By", ""].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {scrapSalesPg.paged.map(s => (
                    <tr key={s.id} className="border-b border-slate-100">
                      <td className="py-3 px-4 text-xs font-mono font-bold text-[#F97316]">{s.voucher_number}</td>
                      <td className="py-3 px-4 text-xs">{(s.sale_date || "").slice(0, 10)}</td>
                      <td className="py-3 px-4 text-sm">{s.buyer_name}<div className="text-[10px] text-slate-400">{s.buyer_contact || ""}</div></td>
                      <td className="py-3 px-4 text-xs">
                        {(s.line_items || []).length} item{(s.line_items || []).length !== 1 ? "s" : ""}
                        <div className="text-[10px] text-slate-400">{(s.line_items || []).map(l => l.asset_code || l.asset_name).join(", ")}</div>
                      </td>
                      <td className="py-3 px-4 text-sm tabular font-bold text-emerald-700">{formatINR(s.total_sale_value)}</td>
                      <td className="py-3 px-4"><StatusBadge status={s.status || "Draft"} /></td>
                      <td className="py-3 px-4 text-xs text-slate-600">{s.remarks || "—"}</td>
                      <td className="py-3 px-4 text-[10px] text-slate-400"><UserLabel value={s.created_by} fallback="—" /></td>
                      <td className="py-3 px-4">
                        <button data-testid={`scrap-sale-pdf-${s.id}`} title="Download voucher PDF"
                          onClick={async () => {
                            try {
                              const res = await api.get(`/scrap-sales/${s.id}/voucher-pdf`, { responseType: "blob" });
                              const url = URL.createObjectURL(res.data);
                              window.open(url, "_blank");
                              setTimeout(() => URL.revokeObjectURL(url), 60000);
                            } catch { toast.error("PDF failed"); }
                          }}
                          className="text-xs font-bold text-[#F97316] hover:underline uppercase">PDF</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={scrapSalesPg.page} pageSize={scrapSalesPg.pageSize} total={scrapSalesPg.total}
              onPage={scrapSalesPg.setPage} onPageSize={scrapSalesPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        ))}

      {tab === "transfers" && (transfers.length === 0
        ? <EmptyState title="No asset transfers yet" hint="Move an Available asset between branches — Draft → Approved → Received." />
        : transfersFiltered.length === 0
        ? <EmptyState title="No matches" hint="Clear the search to see all transfers." />
        : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="asset-transfers-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Transfer No", "Date", "From", "To", "Assets", "Vehicle / LR", "Status", ""].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {transfersPg.paged.map(t => (
                    <tr key={t.id} className="border-b border-slate-100">
                      <td className="py-3 px-4 text-xs font-mono font-bold text-[#F97316]">{t.transfer_number}</td>
                      <td className="py-3 px-4 text-xs">{(t.transfer_date || "").slice(0, 10)}</td>
                      <td className="py-3 px-4 text-xs">{t.from_branch_name}</td>
                      <td className="py-3 px-4 text-xs"><ArrowRightLeft size={11} className="inline mr-1 text-slate-400" />{t.to_branch_name}</td>
                      <td className="py-3 px-4 text-xs">{(t.line_items || []).length}</td>
                      <td className="py-3 px-4 text-[11px] text-slate-500">{t.vehicle_no || "—"}{t.lr_no ? ` · LR ${t.lr_no}` : ""}</td>
                      <td className="py-3 px-4"><StatusBadge status={t.status} /></td>
                      <td className="py-3 px-4 text-xs whitespace-nowrap">
                        {t.status === "Draft" && <button data-testid={`at-approve-${t.id}`} onClick={() => approveTransfer(t.id)} className="text-emerald-700 hover:underline mr-3">Approve</button>}
                        {t.status === "Approved" && <button data-testid={`at-receive-${t.id}`} onClick={() => receiveTransfer(t.id)} className="text-[#F97316] hover:underline mr-3 inline-flex items-center gap-1"><CheckCircle2 size={11} />Receive</button>}
                        {(t.status === "Draft" || t.status === "Approved") && <button data-testid={`at-cancel-${t.id}`} onClick={() => cancelTransfer(t.id)} className="text-red-600 hover:underline">Cancel</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={transfersPg.page} pageSize={transfersPg.pageSize} total={transfersPg.total}
              onPage={transfersPg.setPage} onPageSize={transfersPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        ))}

      {tab === "movements" && (movements.length === 0
        ? <EmptyState title="No asset movements yet" />
        : movementsFiltered.length === 0
        ? <EmptyState title="No matches" hint="Clear the search to see all movements." />
        : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="movements-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Date", "Asset", "Type", "From", "To", "Reference", "Remarks", "By"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {movementsPg.paged.map(m => (
                    <tr key={m.id} className="border-b border-slate-100">
                      <td className="py-3 px-4 text-xs">{(m.movement_date || "").slice(0, 10)}</td>
                      <td className="py-3 px-4 text-xs font-mono font-bold">{m.asset_code}</td>
                      <td className="py-3 px-4 text-xs uppercase font-bold text-slate-700">{m.movement_type}</td>
                      <td className="py-3 px-4 text-xs">{m.from_member_name || "—"}</td>
                      <td className="py-3 px-4 text-xs"><ArrowRight size={10} className="inline mr-1 text-slate-400" />{m.to_member_name || m.to_location || "—"}</td>
                      <td className="py-3 px-4 text-xs font-mono text-slate-500">{m.reference_no || "—"}</td>
                      <td className="py-3 px-4 text-xs text-slate-600">{m.remarks || "—"}</td>
                      <td className="py-3 px-4 text-[10px] text-slate-400"><UserLabel value={m.created_by} fallback="—" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={movementsPg.page} pageSize={movementsPg.pageSize} total={movementsPg.total}
              onPage={movementsPg.setPage} onPageSize={movementsPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        ))}

      {openRepair && <RepairForm onClose={() => { setOpenRepair(false); navigate("/"); }}
        onViewList={() => setOpenRepair(false)}
        onSaved={() => { setOpenRepair(false); loadRepairs(); loadRegister(); loadMovements(); }} />}

      {openScrap && <ScrapForm onClose={() => { setOpenScrap(false); navigate("/"); }}
        onViewList={() => setOpenScrap(false)}
        onSaved={() => { setOpenScrap(false); loadScraps(); loadRegister(); loadMovements(); }} />}
      {openScrapSale && <ScrapSaleForm scraps={scraps}
        onClose={() => setOpenScrapSale(false)}
        onSaved={() => { setOpenScrapSale(false); loadScrapSales(); loadScraps(); loadRegister(); loadMovements(); }} />}

      {openTransfer && <AssetTransferForm onClose={() => { setOpenTransfer(false); navigate("/"); }}
        onViewList={() => setOpenTransfer(false)}
        onSaved={() => { setOpenTransfer(false); loadTransfers(); loadMovements(); }} />}

      {openAdj && <AdjustmentForm onClose={() => setOpenAdj(false)}
        onSaved={() => { setOpenAdj(false); loadAdjustments(); }} />}
    </div>
  );
}

// ============== Repair Form ==============
function RepairForm({ onClose, onViewList, onSaved }) {
  const [assets, setAssets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  // Sprint 4.3 — branch-scoped warehouse pickers (Source + Return).
  // Loaded when the picked asset's branch is known.
  const [warehouses, setWarehouses] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    voucher_date: today, asset_id: "", vendor_id: "", vendor_name: "",
    project_id: "", project_name: "", repair_cost: 0, invoice_ref: "",
    description: "", capitalised: true, marks_in_repair: true,
    // Sprint 4.3 Phase C — warehouse linkage
    from_warehouse_id: "", from_warehouse_name: "",
    return_to_warehouse_id: "", return_to_warehouse_name: "",
  });
  const [saving, setSaving] = useState(false);
  const [vendorSearch, setVendorSearch] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/assets"), api.get("/vendors/picker"), api.get("/projects-picker")]).then(([a, v, p]) => {
      setAssets((a.data || []).filter(x => x.status !== "Scrapped" && x.status !== "Sold"));
      setVendors(v.data); setProjects(p.data);
    });
  }, []);

  // Selected asset's branch (drives the warehouse picker)
  const selectedAsset = assets.find(a => a.id === form.asset_id) || null;
  const assetBranchId = selectedAsset?.branch_id || "";

  // Sprint 4.3 — load warehouses scoped to the asset's branch.
  // Auto-pick the asset's current warehouse as Source and Return defaults.
  useEffect(() => {
    if (!assetBranchId) { setWarehouses([]); return; }
    api.get(`/warehouses/picker?branch_id=${assetBranchId}`).then(w => {
      const wlist = Array.isArray(w.data) ? w.data : [];
      setWarehouses(wlist);
      const currentWhId   = selectedAsset?.warehouse_id || "";
      const currentWhName = selectedAsset?.warehouse_name || "";
      const dflt = wlist.find(x => x.id === currentWhId)
                || wlist.find(x => x.is_default)
                || wlist[0];
      setForm(p => ({
        ...p,
        from_warehouse_id:   p.from_warehouse_id   || currentWhId || dflt?.id || "",
        from_warehouse_name: p.from_warehouse_name || currentWhName || dflt?.warehouse_name || "",
        return_to_warehouse_id:   p.return_to_warehouse_id   || currentWhId || dflt?.id || "",
        return_to_warehouse_name: p.return_to_warehouse_name || currentWhName || dflt?.warehouse_name || "",
      }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetBranchId, form.asset_id]);

  const setF = (k, val) => setForm(p => ({ ...p, [k]: val }));
  const pickVendor = (id) => { const v = vendors.find(x => x.id === id); setForm(p => ({ ...p, vendor_id: id, vendor_name: v?.name || "" })); };
  const pickProject = (id) => { const pr = projects.find(x => x.id === id); setForm(p => ({ ...p, project_id: id, project_name: pr?.project_name || pr?.name || "" })); };

  async function save(e) {
    e.preventDefault();
    if (!form.asset_id) { toast.error("Pick an asset"); return; }
    if (Number(form.repair_cost) < 0) { toast.error("Repair cost cannot be negative"); return; }
    if (!form.vendor_id) { toast.error("Vendor is mandatory — pick from the Vendor master."); return; }
    if (!(form.description || "").trim()) { toast.error("Reason / description is mandatory."); return; }
    // Sprint 4.3 — warehouses mandatory when the asset is sent out for repair
    if (form.marks_in_repair) {
      if (!form.from_warehouse_id) { toast.error("Source Warehouse is mandatory when the asset is sent for repair."); return; }
      if (!form.return_to_warehouse_id) { toast.error("Return Warehouse is mandatory."); return; }
    }
    setSaving(true);
    try { await api.post("/asset-repairs", form); toast.success(`Repair Request created${form.capitalised ? " · capitalised to asset" : ""}`); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <ViewOverlay testid="repair-form-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
        <div>
          <h2 className="font-display text-2xl font-bold">New Repair Voucher</h2>
          <p className="text-xs text-slate-500 mt-0.5">Log a repair cost · Capitalised cost adds to Net Block · Expensed goes to P&amp;L.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="repair-view-list-btn" onClick={onViewList} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">View List</button>
          <button type="button" data-testid="repair-close-btn" onClick={onClose} className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
        </div>
      </div>
      <form onSubmit={save} className="px-8 py-6 max-w-[1100px] mx-auto space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Voucher No (auto · admin can override)">
            <VoucherNumberInput
              module="asset_repair"
              value={form.voucher_number || ""}
              onChange={v => setF("voucher_number", v)}
              testidPrefix="repair-number"
            />
          </Field>
          <Field label="Date *"><input data-testid="repair-date" type="date" required value={form.voucher_date} onChange={e => setF("voucher_date", e.target.value)} className={inputCls} /></Field>
          <Field label="Asset *" full>
            <select data-testid="repair-asset" required value={form.asset_id} onChange={e => setF("asset_id", e.target.value)} className={inputCls}>
              <option value="">Select asset…</option>
              {assets.map(a => <option key={a.id} value={a.id}>{a.asset_code} · {a.name} · {a.branch_name || ""}</option>)}
            </select>
          </Field>
          <Field label="Repair Cost (₹)">
            <input data-testid="repair-cost" type="number" step="0.01" value={form.repair_cost}
              onChange={e => setF("repair_cost", Number(e.target.value))} className={inputCls}
              placeholder="0 if booked separately via PI" />
          </Field>
          <Field label="Vendor *">
            {(() => {
              const vendorLabel = (v) => `${v.name}${v.gst_number ? " · " + v.gst_number : ""}${v.city ? " · " + v.city : ""}`;
              const sel = vendors.find(v => v.id === form.vendor_id);
              const boundLabel = sel ? vendorLabel(sel) : (form.vendor_id ? form.vendor_name : "");
              const displayValue = vendorSearch !== null ? vendorSearch : boundLabel;
              return (
                <>
                  <input data-testid="repair-vendor" type="text" list="repair-vendor-list" required
                    value={displayValue}
                    placeholder={`🔍 Search ${vendors.length} vendors by name / GSTIN / city…`}
                    onChange={e => {
                      const raw = e.target.value || "";
                      setVendorSearch(raw);
                      const v = raw.trim().toLowerCase();
                      const m = vendors.find(x => vendorLabel(x).toLowerCase() === v);
                      if (m) { pickVendor(m.id); setVendorSearch(null); }
                      else if (!v) setForm(p => ({ ...p, vendor_id: "", vendor_name: "" }));
                    }}
                    onBlur={() => setVendorSearch(null)}
                    className={inputCls} />
                  <datalist id="repair-vendor-list">
                    {vendors.map(v => <option key={v.id} value={vendorLabel(v)} />)}
                  </datalist>
                </>
              );
            })()}
          </Field>
          <Field label="Project">
            <select data-testid="repair-project" value={form.project_id} onChange={e => pickProject(e.target.value)} className={inputCls}>
              <option value="">—</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} · {p.project_name || p.name}</option>)}
            </select>
          </Field>
          <Field label="Vendor Invoice / Ref">
            <input data-testid="repair-ref" value={form.invoice_ref} onChange={e => setF("invoice_ref", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Treatment" full>
            <div className="flex gap-4 items-center mt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="repair-capitalised-yes" type="radio" checked={form.capitalised === true} onChange={() => setF("capitalised", true)} />
                <span className="font-bold text-emerald-700">Capitalised</span><span className="text-xs text-slate-500">— add to asset Net Block</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="repair-capitalised-no" type="radio" checked={form.capitalised === false} onChange={() => setF("capitalised", false)} />
                <span className="font-bold text-slate-700">Expensed</span><span className="text-xs text-slate-500">— charge to P&amp;L</span>
              </label>
            </div>
          </Field>
          <Field label="Asset Status" full>
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-1 px-3 py-2 bg-amber-50 border border-amber-200">
              <input data-testid="repair-marks-in-repair" type="checkbox" checked={!!form.marks_in_repair} onChange={e => setF("marks_in_repair", e.target.checked)} />
              <span className="font-bold text-amber-900">Send asset to “In Repair”</span>
              <span className="text-xs text-amber-800 normal-case">— asset is marked unavailable; click &quot;Mark Ready&quot; on the repair row to bring it back.</span>
            </label>
          </Field>
          <Field label="Reason / Description *" full>
            <textarea data-testid="repair-desc" rows={2} required value={form.description} onChange={e => setF("description", e.target.value)} className={inputCls} placeholder="Nature of repair…" />
          </Field>
          {/* Sprint 4.3 Phase C — Warehouse linkage */}
          <Field label="Source Warehouse *">
            <select
              data-testid="repair-from-warehouse"
              required={!!form.marks_in_repair}
              disabled={!form.asset_id || !assetBranchId}
              value={form.from_warehouse_id || ""}
              onChange={e => {
                const w = warehouses.find(x => x.id === e.target.value);
                setForm(p => ({ ...p,
                  from_warehouse_id:   e.target.value,
                  from_warehouse_name: w?.warehouse_name || "",
                }));
              }}
              className={inputCls}
            >
              <option value="">
                {!form.asset_id
                  ? "Pick an asset first"
                  : (warehouses.length ? "Select warehouse…" : "No warehouse available")}
              </option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · Default" : ""}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">Where the asset is currently stored — defaults to the asset&apos;s current warehouse.</p>
          </Field>
          <Field label="Return Warehouse *">
            <select
              data-testid="repair-return-warehouse"
              required={!!form.marks_in_repair}
              disabled={!form.asset_id || !assetBranchId}
              value={form.return_to_warehouse_id || ""}
              onChange={e => {
                const w = warehouses.find(x => x.id === e.target.value);
                setForm(p => ({ ...p,
                  return_to_warehouse_id:   e.target.value,
                  return_to_warehouse_name: w?.warehouse_name || "",
                }));
              }}
              className={inputCls}
            >
              <option value="">
                {!form.asset_id
                  ? "Pick an asset first"
                  : (warehouses.length ? "Select warehouse…" : "No warehouse available")}
              </option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · Default" : ""}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">Where the asset returns after repair — defaults to Source Warehouse.</p>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="save-repair-btn" disabled={saving} type="submit" className="px-6 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase disabled:opacity-50">{saving ? "Saving…" : "Save Voucher"}</button>
        </div>
      </form>
    </ViewOverlay>
  );
}

// ============== Scrap Form ==============
// Jul-2026 — batch-sell scrapped assets (user request). Marks scraps as
// sold, flips assets to Sold and clears them from the warehouse. The JV
// for sale proceeds is passed manually — this posts NOTHING to the GL.
function ScrapSaleForm({ scraps, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ sale_date: today, buyer_name: "", buyer_contact: "", remarks: "" });
  const [selected, setSelected] = useState({});   // scrap_id -> sale_value
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const eligible = useMemo(() => {
    const pool = scraps.filter(s => !s.sold);
    const q = query.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(s => `${s.voucher_number || ""} ${s.asset_code || ""} ${s.asset_name || ""}`.toLowerCase().includes(q));
  }, [scraps, query]);

  const toggle = (s) => setSelected(p => {
    const n = { ...p };
    if (n[s.id] !== undefined) delete n[s.id];
    else n[s.id] = s.salvage_recovered || 0;
    return n;
  });
  const total = Object.values(selected).reduce((a, v) => a + (parseFloat(v) || 0), 0);
  const count = Object.keys(selected).length;

  async function save(e) {
    e.preventDefault();
    if (!count) { toast.error("Select at least one scrapped asset"); return; }
    setSaving(true);
    try {
      const r = await api.post("/scrap-sales", {
        ...form,
        line_items: Object.entries(selected).map(([scrap_id, sale_value]) => ({
          scrap_id, sale_value: parseFloat(sale_value) || 0,
        })),
      });
      toast.success(r.data?.status === "Approved"
        ? "Scrap sale approved — assets cleared from warehouse"
        : "Scrap sale submitted for approval");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <ViewOverlay testid="scrap-sale-form-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
        <div>
          <h2 className="font-display text-2xl font-bold">New Scrap Sale (Batch)</h2>
          <p className="text-xs text-slate-500 mt-0.5">Sell multiple scrapped assets in one voucher · clears them from the warehouse · pass the JV for proceeds manually.</p>
        </div>
        <button type="button" data-testid="scrap-sale-close-btn" onClick={onClose} className="p-1.5 hover:bg-slate-100"><X size={18} /></button>
      </div>
      <form onSubmit={save} className="px-8 py-6 max-w-[1000px] mx-auto space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Sale Date *"><input data-testid="scrap-sale-date" type="date" required value={form.sale_date} onChange={e => setF("sale_date", e.target.value)} className={inputCls} /></Field>
          <Field label="Buyer Name *"><input data-testid="scrap-sale-buyer" required value={form.buyer_name} onChange={e => setF("buyer_name", e.target.value)} placeholder="e.g. Gupta Scrap Traders" className={inputCls} /></Field>
          <Field label="Buyer Contact"><input data-testid="scrap-sale-contact" value={form.buyer_contact} onChange={e => setF("buyer_contact", e.target.value)} placeholder="Phone / GSTIN (optional)" className={inputCls} /></Field>
          <Field label="Remarks" full><input data-testid="scrap-sale-remarks" value={form.remarks} onChange={e => setF("remarks", e.target.value)} placeholder="e.g. Lot sold by weight — 45kg mixed metal" className={inputCls} /></Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Scrapped assets available to sell ({eligible.length})</div>
            <input data-testid="scrap-sale-search" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search scrap voucher / asset…" className="border border-slate-300 px-3 py-1.5 text-xs w-64 focus:outline-none focus:border-orange-500" />
          </div>
          <div className="border border-slate-200 max-h-72 overflow-y-auto" data-testid="scrap-sale-eligible-list">
            {eligible.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-slate-400">No unsold scrapped assets{query ? ` match “${query}”` : ""}.</div>
            ) : eligible.map(s => {
              const on = selected[s.id] !== undefined;
              return (
                <div key={s.id} className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0 ${on ? "bg-orange-50/60" : "hover:bg-slate-50"}`}>
                  <input type="checkbox" data-testid={`scrap-sale-pick-${s.asset_code || s.id}`} checked={on} onChange={() => toggle(s)} className="accent-[#F97316]" />
                  <div className="flex-1 text-sm">
                    {s.item_type === "Inventory" ? (
                      <>
                        <b>{s.asset_name}</b>
                        <span className="ml-2 inline-block bg-sky-100 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[9px] font-bold uppercase">Inventory</span>
                        <span className="ml-2 font-mono text-[10px] text-slate-500">{s.voucher_number}</span>
                        <div className="text-[10px] text-slate-400">{s.quantity} {s.unit} · write-off {formatINR(s.book_value_at_scrap)} · {s.reason || ""}</div>
                      </>
                    ) : (
                      <>
                        <b>{s.asset_code}</b> · {s.asset_name}
                        <span className="ml-2 font-mono text-[10px] text-slate-500">{s.voucher_number}</span>
                        <div className="text-[10px] text-slate-400">Book value at scrap: {formatINR(s.book_value_at_scrap)} · {s.reason || ""}</div>
                      </>
                    )}
                  </div>
                  {on && (
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-slate-500">Sale ₹</span>
                      <input data-testid={`scrap-sale-value-${s.asset_code || s.id}`} type="number" min="0" step="0.01"
                        value={selected[s.id]}
                        onChange={e => setSelected(p => ({ ...p, [s.id]: e.target.value }))}
                        className="w-28 border border-slate-300 px-2 py-1.5 text-right focus:outline-none focus:border-orange-500" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 pt-4">
          <div className="text-sm" data-testid="scrap-sale-total">
            <span className="text-slate-500">{count} asset{count !== 1 ? "s" : ""} selected · Total sale value:</span>{" "}
            <b className="text-emerald-700">{formatINR(total)}</b>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" data-testid="scrap-sale-save-btn" disabled={saving || !count}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold disabled:opacity-50">
              {saving ? "Posting…" : "Post Scrap Sale"}
            </button>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 text-xs text-amber-800 p-3">
          <b>Note:</b> Posting clears the selected assets from the warehouse (status → Sold) and prints a batch voucher.
          No accounting entry is made — pass the Journal Voucher for the sale proceeds manually.
        </div>
      </form>
    </ViewOverlay>
  );
}


function ScrapForm({ onClose, onViewList, onSaved }) {
  const [assets, setAssets] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ voucher_date: today, asset_id: "", reason: "", salvage_recovered: 0 });
  const [saving, setSaving] = useState(false);
  const [assetQuery, setAssetQuery] = useState("");
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    api.get("/assets").then(a => setAssets((a.data || []).filter(x => x.status !== "Scrapped" && x.status !== "Sold")));
  }, []);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const sel = useMemo(() => assets.find(a => a.id === form.asset_id), [assets, form.asset_id]);

  // Jul-2026 — searchable asset picker (user request): filter by code,
  // name, branch or status instead of scrolling a giant dropdown.
  const matches = useMemo(() => {
    const q = assetQuery.trim().toLowerCase();
    const pool = q
      ? assets.filter(a => `${a.asset_code || ""} ${a.name || ""} ${a.branch_name || ""} ${a.status || ""}`.toLowerCase().includes(q))
      : assets;
    return pool.slice(0, 50);
  }, [assets, assetQuery]);

  async function save(e) {
    e.preventDefault();
    if (!form.asset_id) { toast.error("Pick an asset"); return; }
    if (!form.reason) { toast.error("Reason is required"); return; }
    if (!confirm("Mark this asset as Scrapped? This is permanent.")) return;
    setSaving(true);
    try { await api.post("/asset-scraps", form); toast.success("Asset scrapped"); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <ViewOverlay testid="scrap-form-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
        <div>
          <h2 className="font-display text-2xl font-bold">New Scrap Voucher</h2>
          <p className="text-xs text-slate-500 mt-0.5">Write-off the asset&apos;s book value to P&amp;L · Salvage value reduces the net loss.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="scrap-view-list-btn" onClick={onViewList} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">View List</button>
          <button type="button" data-testid="scrap-close-btn" onClick={onClose} className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
        </div>
      </div>
      <form onSubmit={save} className="px-8 py-6 max-w-[900px] mx-auto space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Voucher No (auto · admin can override)">
            <VoucherNumberInput
              module="asset_scrap"
              value={form.voucher_number || ""}
              onChange={v => setF("voucher_number", v)}
              testidPrefix="scrap-number"
            />
          </Field>
          <Field label="Date *"><input data-testid="scrap-date" type="date" required value={form.voucher_date} onChange={e => setF("voucher_date", e.target.value)} className={inputCls} /></Field>
          <Field label="Asset * (type to search)" full>
            {sel ? (
              <div className="flex items-center justify-between border border-slate-300 px-3 py-2 bg-slate-50" data-testid="scrap-asset-selected">
                <span className="text-sm">
                  <b>{sel.asset_code}</b> · {sel.name}
                  <span className="ml-1 text-xs text-slate-500">· {sel.branch_name || "—"} · {sel.status}</span>
                </span>
                <button type="button" data-testid="scrap-asset-clear" title="Change asset"
                  className="text-slate-400 hover:text-red-600"
                  onClick={() => { setF("asset_id", ""); setAssetQuery(""); setShowList(false); }}>
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input data-testid="scrap-asset-search" value={assetQuery} autoComplete="off"
                  onChange={e => { setAssetQuery(e.target.value); setShowList(true); }}
                  onFocus={() => setShowList(true)}
                  placeholder="Search by asset code, name, branch or status…" className={inputCls} />
                {showList && (
                  <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-slate-300 shadow-lg" data-testid="scrap-asset-options">
                    {matches.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-400">No assets match “{assetQuery}”</div>
                    ) : matches.map(a => (
                      <button type="button" key={a.id} data-testid={`scrap-asset-opt-${a.asset_code}`}
                        onClick={() => { setF("asset_id", a.id); setShowList(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50 border-b border-slate-100 last:border-b-0">
                        <b>{a.asset_code}</b> · {a.name}
                        <span className="ml-1 text-[10px] uppercase font-bold text-slate-400">{a.branch_name || ""} · {a.status}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Field>
          <Field label="Salvage Recovered (₹)">
            <input data-testid="scrap-salvage" type="number" step="0.01" value={form.salvage_recovered}
              onChange={e => setF("salvage_recovered", Number(e.target.value))} className={inputCls} />
          </Field>
          <Field label="Reason *" full>
            <textarea data-testid="scrap-reason" required rows={2} value={form.reason} onChange={e => setF("reason", e.target.value)} className={inputCls} placeholder="Beyond repair, obsolete, etc." />
          </Field>
          {sel && (
            <div className="md:col-span-3 bg-amber-50 border border-amber-300 px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-900">Asset Snapshot</div>
              <div className="mt-1 text-sm">
                <span className="font-bold">{sel.asset_code}</span> · {sel.name} · Purchase Value: <span className="tabular font-bold">{formatINR(sel.purchase_value || 0)}</span>
              </div>
              <div className="text-[11px] text-amber-800 mt-1">Net loss is computed server-side as (book value − salvage recovered).</div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="save-scrap-btn" disabled={saving} type="submit" className="px-6 py-2.5 bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase disabled:opacity-50 flex items-center gap-2"><Trash2 size={14} />{saving ? "Saving…" : "Scrap Asset"}</button>
        </div>
      </form>
    </ViewOverlay>
  );
}

// ============== Asset Transfer Form ==============
function AssetTransferForm({ onClose, onViewList, onSaved }) {
  const [branches, setBranches] = useState([]);
  const [assets, setAssets] = useState([]);
  // Sprint 2026-Q1d / Step ② — warehouse pickers for the from / to legs.
  const [warehouses, setWarehouses] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    transfer_date: today, from_branch_id: "", to_branch_id: "",
    from_warehouse_id: "", from_warehouse_name: "",
    to_warehouse_id: "", to_warehouse_name: "",
    vehicle_no: "", driver_name: "", lr_no: "", notes: "",
    line_items: [],
  });
  const [saving, setSaving] = useState(false);
  const [searchTxt, setSearchTxt] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/branches"),
      api.get("/assets"),
      api.get("/warehouses").catch(() => ({ data: [] })),
    ]).then(([b, a, w]) => {
      setBranches(b.data);
      setAssets(a.data);
      setWarehouses(w.data || []);
    });
  }, []);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Warehouses filtered by branch, only active ones — used by both
  // pickers. The auto-default-on-branch-pick effect (below) chooses
  // the row with is_default=true, or the first active row otherwise.
  const fromBranchWarehouses = useMemo(
    () => warehouses.filter(w => w.branch_id === form.from_branch_id && w.is_active !== false),
    [warehouses, form.from_branch_id]
  );
  const toBranchWarehouses = useMemo(
    () => warehouses.filter(w => w.branch_id === form.to_branch_id && w.is_active !== false),
    [warehouses, form.to_branch_id]
  );

  // Whenever either branch changes, auto-pick that branch's default
  // warehouse so the operator doesn't have to. They can still override
  // by clicking the dropdown.
  useEffect(() => {
    if (!form.from_branch_id) { setF("from_warehouse_id", ""); setF("from_warehouse_name", ""); return; }
    const def = fromBranchWarehouses.find(w => w.is_default) || fromBranchWarehouses[0];
    if (def) {
      setForm(p => ({ ...p, from_warehouse_id: def.id, from_warehouse_name: def.warehouse_name || "" }));
    } else {
      setForm(p => ({ ...p, from_warehouse_id: "", from_warehouse_name: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.from_branch_id, fromBranchWarehouses.length]);

  useEffect(() => {
    if (!form.to_branch_id) { setF("to_warehouse_id", ""); setF("to_warehouse_name", ""); return; }
    const def = toBranchWarehouses.find(w => w.is_default) || toBranchWarehouses[0];
    if (def) {
      setForm(p => ({ ...p, to_warehouse_id: def.id, to_warehouse_name: def.warehouse_name || "" }));
    } else {
      setForm(p => ({ ...p, to_warehouse_id: "", to_warehouse_name: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.to_branch_id, toBranchWarehouses.length]);

  const branchAssets = useMemo(
    () => assets.filter(a => a.branch_id === form.from_branch_id && a.status === "Available"),
    [assets, form.from_branch_id]
  );

  function addLine() { setForm(p => ({ ...p, line_items: [...p.line_items, { asset_id: "", asset_code: "", asset_name: "", condition_at_dispatch: "Good" }] })); }
  function removeLine(i) { setForm(p => ({ ...p, line_items: p.line_items.filter((_, j) => j !== i) })); }
  function pickAsset(i, aid) {
    const a = branchAssets.find(x => x.id === aid);
    if (!a) return;
    setForm(p => ({ ...p, line_items: p.line_items.map((l, j) => j === i ? { ...l, asset_id: a.id, asset_code: a.asset_code, asset_name: a.name } : l) }));
  }
  function updCondition(i, val) { setForm(p => ({ ...p, line_items: p.line_items.map((l, j) => j === i ? { ...l, condition_at_dispatch: val } : l) })); }

  const assetLabel = (a) => `${a.asset_code} · ${a.name}${a.serial_no ? " · SN " + a.serial_no : ""}`;

  async function save(e) {
    e.preventDefault();
    if (!form.from_branch_id || !form.to_branch_id) { toast.error("Pick both branches"); return; }
    if (form.from_branch_id === form.to_branch_id) { toast.error("From and To branches must differ"); return; }
    if (form.line_items.length === 0) { toast.error("Add at least one asset"); return; }
    for (let i = 0; i < form.line_items.length; i++) {
      if (!form.line_items[i].asset_id) { toast.error(`Line ${i + 1}: pick an asset`); return; }
    }
    setSaving(true);
    try { await api.post("/asset-transfers", form); toast.success("Asset transfer created (Draft)"); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <ViewOverlay testid="at-form-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
        <div>
          <h2 className="font-display text-2xl font-bold">New Asset Transfer</h2>
          <p className="text-xs text-slate-500 mt-0.5">Only Available assets at the source branch can be transferred · Draft → Approved (In-Transit) → Received.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="at-view-list-btn" onClick={onViewList} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">View List</button>
          <button type="button" data-testid="at-close-btn" onClick={onClose} className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
        </div>
      </div>
      <form onSubmit={save} className="px-8 py-6 max-w-[1500px] mx-auto space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Transfer No (auto · admin can override)">
            <VoucherNumberInput
              module="asset_transfer"
              value={form.transfer_number || ""}
              onChange={v => setF("transfer_number", v)}
              testidPrefix="at-number"
            />
          </Field>
          <Field label="Date *"><input data-testid="at-date" type="date" required value={form.transfer_date} onChange={e => setF("transfer_date", e.target.value)} className={inputCls} /></Field>
          <Field label="From Branch *">
            <select data-testid="at-from" required value={form.from_branch_id}
              onChange={e => { setF("from_branch_id", e.target.value); setF("line_items", []); }} className={inputCls}>
              <option value="">Select…</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
            </select>
          </Field>
          <Field label="To Branch *">
            <select data-testid="at-to" required value={form.to_branch_id}
              onChange={e => setF("to_branch_id", e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {branches.filter(b => b.id !== form.from_branch_id).map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
            </select>
          </Field>
          {/* Sprint 2026-Q1d / Step ② — Warehouse legs. Defaults to each
              branch's default warehouse on branch pick; user can change
              either before saving. Empty option allowed so server-side
              auto-resolve still works for legacy flows. */}
          <Field label="From Warehouse">
            <select
              data-testid="at-from-wh"
              value={form.from_warehouse_id}
              onChange={e => {
                const wh = fromBranchWarehouses.find(w => w.id === e.target.value);
                setForm(p => ({
                  ...p,
                  from_warehouse_id:   wh?.id || "",
                  from_warehouse_name: wh?.warehouse_name || "",
                }));
              }}
              className={inputCls}
              disabled={!form.from_branch_id}
            >
              <option value="">— default —</option>
              {fromBranchWarehouses.map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_name}{w.is_default ? " · default" : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="To Warehouse">
            <select
              data-testid="at-to-wh"
              value={form.to_warehouse_id}
              onChange={e => {
                const wh = toBranchWarehouses.find(w => w.id === e.target.value);
                setForm(p => ({
                  ...p,
                  to_warehouse_id:   wh?.id || "",
                  to_warehouse_name: wh?.warehouse_name || "",
                }));
              }}
              className={inputCls}
              disabled={!form.to_branch_id}
            >
              <option value="">— default —</option>
              {toBranchWarehouses.map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_name}{w.is_default ? " · default" : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reference / Notes">
            <input data-testid="at-notes" value={form.notes} onChange={e => setF("notes", e.target.value)} className={inputCls} />
          </Field>
        </div>

        <details className="border border-slate-200 bg-slate-50/40 px-4 py-3" data-testid="at-transport-section">
          <summary className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 cursor-pointer select-none">Transportation Details</summary>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <Field label="Vehicle No"><input data-testid="at-vehicle" value={form.vehicle_no} onChange={e => setF("vehicle_no", e.target.value)} className={inputCls} /></Field>
            <Field label="Driver"><input data-testid="at-driver" value={form.driver_name} onChange={e => setF("driver_name", e.target.value)} className={inputCls} /></Field>
            <Field label="LR / Docket No"><input data-testid="at-lr" value={form.lr_no} onChange={e => setF("lr_no", e.target.value)} className={inputCls} /></Field>
          </div>
        </details>

        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Assets ({branchAssets.length} available at source)</h3>
            <button type="button" data-testid="at-add-line" onClick={addLine} disabled={!form.from_branch_id}
              className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"><Plus size={11} /> Add Asset</button>
          </div>
          {form.from_branch_id && branchAssets.length === 0 && (
            <p className="text-xs text-amber-700 italic">No Available assets at this branch — assign an asset to it first (Assets → Issue Voucher with Member at this branch).</p>
          )}
          {form.line_items.length > 0 && (
            <table className="w-full text-sm" data-testid="at-lines-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>{["#", "Asset", "Condition", ""].map(h => <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-2">{h}</th>)}</tr>
              </thead>
              <tbody>
                {form.line_items.map((li, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 px-2 text-xs text-slate-500">{i + 1}</td>
                    <td className="py-2 px-2">
                      <input data-testid={`at-asset-${i}`} type="text"
                        list={`at-assets-list-${i}`}
                        value={searchTxt[i] !== undefined ? searchTxt[i] : (li.asset_id ? (branchAssets.find(a => a.id === li.asset_id) ? assetLabel(branchAssets.find(a => a.id === li.asset_id)) : `${li.asset_code} · ${li.asset_name}`) : "")}
                        onChange={e => {
                          const v = e.target.value;
                          setSearchTxt(prev => ({ ...prev, [i]: v }));
                          const match = branchAssets.find(a => assetLabel(a).toLowerCase() === (v || "").trim().toLowerCase());
                          if (match) {
                            pickAsset(i, match.id);
                            setSearchTxt(prev => { const n = { ...prev }; delete n[i]; return n; });
                          }
                        }}
                        onFocus={() => setSearchTxt(prev => ({ ...prev, [i]: "" }))}
                        onBlur={() => setSearchTxt(prev => { const n = { ...prev }; delete n[i]; return n; })}
                        className={inputCls}
                        placeholder="Search asset by code, name, serial…" />
                      <datalist id={`at-assets-list-${i}`}>
                        {branchAssets.map(a => <option key={a.id} value={assetLabel(a)} />)}
                      </datalist>
                    </td>
                    <td className="py-2 px-2">
                      <select data-testid={`at-condition-${i}`} value={li.condition_at_dispatch} onChange={e => updCondition(i, e.target.value)} className={`${inputCls} w-40`}>
                        {["Good", "Working", "Needs Repair", "Damaged"].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <button type="button" onClick={() => removeLine(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="save-at-btn" disabled={saving} type="submit" className="px-6 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase disabled:opacity-50">{saving ? "Saving…" : "Save (Draft)"}</button>
        </div>
      </form>
    </ViewOverlay>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Sprint 2026-Q1d / Asset Depreciation Step ⑥ — Period Adjustment Journal form
// ─────────────────────────────────────────────────────────────────────────────
// Posts an append-only adjustment row into `depreciation_adjustments`. The
// row carries the same 7-dim vocabulary as the immutable snapshots so
// reports can union the two collections on identical keys.
//
// Two modes share this single form:
//   • Asset-specific  — pick an asset → all 7 dims resolve server-side.
//   • Overhead / non-asset — leave asset blank → caller must tag at least
//     one of {project, branch, cost_centre, business_vertical}.
//
// Validation mirrors the backend exactly so the user sees errors inline
// rather than after a network round-trip.

function AdjustmentForm({ onClose, onSaved }) {
  const [assets, setAssets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [form, setForm] = useState({
    period_key: new Date().toISOString().slice(0, 7),
    asset_id: "",
    delta: "",
    reason: "",
    reference_doc: "",
    // Overhead-mode dims — used only when asset_id is blank
    project_id: "",
    branch_id: "",
    cost_centre_id: "",
    business_vertical_name: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/assets?limit=2000").then(r => {
      const d = r.data;
      setAssets(Array.isArray(d) ? d : (d?.items || []));
    }).catch(() => setAssets([]));
    api.get("/projects-picker").then(r => {
      const d = r.data;
      setProjects(Array.isArray(d) ? d : (d?.items || []));
    }).catch(() => setProjects([]));
    api.get("/branches").then(r => setBranches(r.data || [])).catch(() => setBranches([]));
    api.get("/mca/cost-centres").then(r => {
      const d = r.data;
      setCostCentres(Array.isArray(d) ? d : (d?.items || []));
    }).catch(() => setCostCentres([]));
  }, []);

  const isOverhead = !form.asset_id;
  const overheadNeeds = isOverhead && !form.project_id && !form.branch_id
    && !form.cost_centre_id && !form.business_vertical_name;

  async function save(e) {
    e.preventDefault();
    if (!/^\d{4}-\d{2}$/.test(form.period_key)) {
      toast.error("Period must be YYYY-MM (e.g. 2026-02).");
      return;
    }
    const delta = parseFloat(form.delta);
    if (!delta || Number.isNaN(delta)) {
      toast.error("Δ Depreciation must be a non-zero number.");
      return;
    }
    if ((form.reason || "").trim().length < 8) {
      toast.error("Reason must be at least 8 characters.");
      return;
    }
    if (overheadNeeds) {
      toast.error("For overhead adjustments, tag at least one of: project, branch, cost centre, vertical.");
      return;
    }
    const payload = {
      period_key:    form.period_key,
      delta:         delta,
      reason:        form.reason.trim(),
      reference_doc: form.reference_doc.trim(),
    };
    if (form.asset_id) {
      payload.asset_id = form.asset_id;
    } else {
      // Server validates at least one of these is set
      if (form.project_id) {
        const p = projects.find(x => x.id === form.project_id);
        payload.project_id = form.project_id;
        payload.project_name = p?.project_name || "";
        // Bonus — propagate the project's CC + vertical for richer reports
        if (p?.cost_centre_id) {
          payload.cost_centre_id = p.cost_centre_id;
          payload.cost_centre_name = p.cost_centre_name || "";
        }
        if (p?.business_vertical_name || p?.work_type) {
          payload.business_vertical_id   = p.business_vertical_id || "";
          payload.business_vertical_name = p.business_vertical_name || p.work_type || "";
        }
        if (p?.branch_id) {
          payload.branch_id   = p.branch_id;
          payload.branch_name = p.branch_name || "";
        }
      }
      if (form.branch_id && !payload.branch_id) {
        const b = branches.find(x => x.id === form.branch_id);
        payload.branch_id   = form.branch_id;
        payload.branch_name = b?.branch_name || "";
      }
      if (form.cost_centre_id && !payload.cost_centre_id) {
        const c = costCentres.find(x => x.id === form.cost_centre_id);
        payload.cost_centre_id   = form.cost_centre_id;
        payload.cost_centre_name = c?.name || "";
      }
      if (form.business_vertical_name && !payload.business_vertical_name) {
        payload.business_vertical_name = form.business_vertical_name;
      }
    }
    try {
      setSaving(true);
      await api.post("/depreciation/adjustments", payload);
      toast.success(`Adjustment posted for ${form.period_key}.`);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post adjustment.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ViewOverlay onClose={onClose}>
      <Toaster richColors position="top-right" />
      <form onSubmit={save} data-testid="adjustment-form" className="space-y-5 p-6 bg-white max-w-3xl mx-auto">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            Depreciation · Step ⑥
          </div>
          <div className="font-display text-xl font-black text-slate-900">
            New Period Adjustment Journal Entry
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Append-only · The immutable snapshot is never modified · Reports compute&nbsp;
            <code className="text-slate-700 bg-slate-100 px-1">snapshots + adjustments</code>.
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Period (YYYY-MM) *">
            <input
              data-testid="adj-period-input"
              type="month"
              value={form.period_key}
              onChange={e => setForm({ ...form, period_key: e.target.value })}
              className={inputCls}
              required
            />
          </Field>
          <Field label="Δ Depreciation (₹) *">
            <input
              data-testid="adj-delta-input"
              type="number"
              step="0.01"
              value={form.delta}
              onChange={e => setForm({ ...form, delta: e.target.value })}
              placeholder="e.g. -250.50 (reduces) or 250.50 (adds)"
              className={inputCls}
              required
            />
          </Field>
          <Field label="Reference Doc">
            <input
              data-testid="adj-ref-input"
              value={form.reference_doc}
              onChange={e => setForm({ ...form, reference_doc: e.target.value })}
              placeholder="e.g. AUDIT-FIX-2026-001"
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Asset (optional — leave blank for overhead adjustments)">
          <select
            data-testid="adj-asset-input"
            value={form.asset_id}
            onChange={e => setForm({ ...form, asset_id: e.target.value })}
            className={inputCls}
          >
            <option value="">— No asset (overhead / general) —</option>
            {assets.map(a => (
              <option key={a.id} value={a.id}>
                {a.asset_code} · {a.name}{a.category ? ` · ${a.category}` : ""}
              </option>
            ))}
          </select>
        </Field>

        {isOverhead && (
          <div className={`p-3 border rounded space-y-3 ${overheadNeeds ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-200"}`}>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700">
              Overhead adjustment — tag at least one dimension *
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Project">
                <select
                  data-testid="adj-overhead-project"
                  value={form.project_id}
                  onChange={e => setForm({ ...form, project_id: e.target.value })}
                  className={inputCls}>
                  <option value="">—</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_code} · {p.project_name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Branch">
                <select
                  data-testid="adj-overhead-branch"
                  value={form.branch_id}
                  onChange={e => setForm({ ...form, branch_id: e.target.value })}
                  className={inputCls}>
                  <option value="">—</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.branch_name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Cost Centre">
                <select
                  data-testid="adj-overhead-cc"
                  value={form.cost_centre_id}
                  onChange={e => setForm({ ...form, cost_centre_id: e.target.value })}
                  className={inputCls}>
                  <option value="">—</option>
                  {costCentres.map(c => (
                    <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Business Vertical (free text)">
                <input
                  data-testid="adj-overhead-vertical"
                  value={form.business_vertical_name}
                  onChange={e => setForm({ ...form, business_vertical_name: e.target.value })}
                  placeholder="e.g. MMG, Civil, IFMS, Solar"
                  className={inputCls}
                />
              </Field>
            </div>
          </div>
        )}

        <Field label="Reason * (min 8 characters — visible in audit history)">
          <textarea
            data-testid="adj-reason-input"
            rows={3}
            value={form.reason}
            onChange={e => setForm({ ...form, reason: e.target.value })}
            placeholder="e.g. Asset useful life originally 10 yrs; auditor revised to 8 yrs; correcting Apr-2026 depreciation by ₹250.50 less."
            className={inputCls}
            required
          />
        </Field>

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button type="button" onClick={onClose}
                  className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            data-testid="adj-save-btn"
            type="submit"
            disabled={saving || overheadNeeds}
            className="px-6 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase disabled:opacity-50">
            {saving ? "Posting…" : "Post Adjustment"}
          </button>
        </div>
      </form>
    </ViewOverlay>
  );
}
